// src/crypt/cryptoUtils.js

// --- Configuration ---
// WARNING: This key is hardcoded. Provides obfuscation ONLY for local export/import.
const SECRET_KEY_STRING = "your-super-secret-key-for-local-use"; // CHANGE THIS
const STATIC_SALT = "a-fixed-salt-value-for-local-export"; // Static salt for this specific use case
let derivedCryptoKey; // Cache for the derived key

// --- Helper Functions (Keep arrayBufferToBase64, base64ToArrayBuffer) ---
// Convert ArrayBuffer to Base64 string
function arrayBufferToBase64(buffer) {
  // Basic check for valid input
  if (!buffer || !(buffer instanceof ArrayBuffer)) {
      console.error("arrayBufferToBase64 received invalid input:", buffer);
      // Optionally throw an error or return an empty string/null
      // Returning undefined is what caused the previous issue. Let's throw.
      throw new Error("arrayBufferToBase64 received invalid input");
  }
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  try {
      // window.btoa is the built-in function for Base64 encoding
      return window.btoa(binary);
  } catch (e) {
      console.error("Error during Base64 encoding (btoa):", e);
      // Rethrow or handle appropriately
      throw new Error("Base64 encoding failed");
  }
}

// Convert Base64 string to ArrayBuffer
function base64ToArrayBuffer(base64) {
  try {
      // window.atob is the built-in function for Base64 decoding
      const binary_string = window.atob(base64);
      const len = binary_string.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binary_string.charCodeAt(i);
      }
      return bytes.buffer;
  } catch (e) {
      console.error("Error during Base64 decoding (atob):", e);
      // Often happens if the input string is not valid Base64
      throw new Error("Base64 decoding failed. Input may be corrupted or invalid.");
  }
}

// --- Core Crypto Functions ---

// Key derivation for hardcoded secret (local file import)
async function getDerivedKeyForLocal() { // Renamed for clarity
  if (!derivedCryptoKey) {
    const keyMaterial = await window.crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(SECRET_KEY_STRING),
    { name: "PBKDF2" },
    false,
    ["deriveKey"]
    );
    // Use the predefined STATIC_SALT
    const saltBuffer = new TextEncoder().encode(STATIC_SALT);
    
    derivedCryptoKey = await window.crypto.subtle.deriveKey(
      {
        name: "PBKDF2",
        salt: saltBuffer,
        iterations: 100000, // Iterations for PBKDF2
        hash: "SHA-256",
      },
      keyMaterial,
      { name: "AES-GCM", length: 256 }, // Key type for AES-GCM
      false, // Exportable (false is generally better if not needed)
      ["encrypt", "decrypt"] // Key usages
    );
  }
  return derivedCryptoKey;
}

// NEW: Key derivation for password-based secure storage
async function deriveKeyFromPassword(password, saltBuffer) {
    const keyMaterial = await window.crypto.subtle.importKey(
        "raw",
        new TextEncoder().encode(password),
        { name: "PBKDF2" },
        false,
        ["deriveKey"]
    );
    return window.crypto.subtle.deriveKey(
        {
            name: "PBKDF2",
            salt: saltBuffer,
            iterations: 250000, // Higher iterations for password-based keys
            hash: "SHA-256",
        },
        keyMaterial,
        { name: "AES-GCM", length: 256 },
        true, // Typically false if not exporting the key itself
        ["encrypt", "decrypt"]
    );
}

/**
 * Encrypts credentials for various purposes (local file export or secure storage).
 *
 * @param {object} dataToEncrypt - The JSON object to encrypt (e.g., { userId, apiKey }).
 * @param {object} options - Configuration for encryption.
 * @param {'local-export' | 'secure-storage'} options.mode - The encryption mode.
 * @param {string} [options.password] - The user's password, required if mode is "secure-storage".
 *
 * @returns {Promise<object>} - The encrypted payload object.
 *   - For local-export: { type: "local-obfuscated", version, iv, encryptedData }
 *   - For secure-storage: { type: "secure-storage", version, salt, iv, encryptedData }
 */
export async function encryptCredentials(dataToEncrypt, options) {
  if (!options || !options.mode) {
    throw new Error("Encryption mode is required in options.");
  }

  let key;
  let saltForPayload = null; // Will only be set for secure-storage
  const iv = window.crypto.getRandomValues(new Uint8Array(12)); // Common: always generate a random IV

  try {
    if (options.mode === 'local-export') {
      key = await getDerivedKeyForLocal();
    } else if (options.mode === 'secure-storage') {
      if (!options.password) {
        throw new Error("Password is required for 'secure-storage' encryption mode.");
      }
      const saltBuffer = window.crypto.getRandomValues(new Uint8Array(16)); // Generate random salt for this encryption
      key = await deriveKeyFromPassword(options.password, saltBuffer);
      saltForPayload = arrayBufferToBase64(saltBuffer); // Store the salt (as base64) in the payload
    } else {
      throw new Error(`Unsupported encryption mode: ${options.mode}`);
    }

    const dataString = JSON.stringify(dataToEncrypt);
    const encodedData = new TextEncoder().encode(dataString);

    const encryptedBuffer = await window.crypto.subtle.encrypt(
      { name: "AES-GCM", iv: iv },
      key,
      encodedData
    );

    const base64Iv = arrayBufferToBase64(iv.buffer);
    const base64EncryptedData = arrayBufferToBase64(encryptedBuffer);

    // Construct the payload based on the mode
    const payload = {
      version: "1.0", // Common versioning
      iv: base64Iv,
      encryptedData: base64EncryptedData,
    };

    if (options.mode === 'local-export') {
      payload.type = "local-obfuscated";
    } else if (options.mode === 'secure-storage') {
      payload.type = "secure-storage";
      payload.salt = saltForPayload; // Add the salt only for secure storage
    }

    return payload;

  } catch (error) {
    console.error(`Encryption failed for mode "${options.mode}":`, error);
    throw new Error(`Encryption failed: ${error.message || "Unknown error"}`);
  }
}

/**
 * Decrypts credentials from various sources (local file or secure storage).
 * @param {object} payload - The encrypted data object.
 *   - For local: { type: "local-obfuscated", iv, encryptedData, version? }
 *   - For secure: { type: "secure-storage", iv, salt, encryptedData, version? }
 * @param {string} [password] - Optional: The user's password, required if payload.type is "secure-storage".
 * @returns {Promise<object>} - The decrypted JSON object (e.g., { userId, apiKey }).
 */
export async function decryptCredentials(payload, password) {
  if (!payload || !payload.type || !payload.encryptedData || !payload.iv) {
    throw new Error("Invalid payload structure: missing type, encryptedData, or iv.");
  }

  let key;
  const iv = base64ToArrayBuffer(payload.iv);
  const encryptedData = base64ToArrayBuffer(payload.encryptedData);

  try {
    if (payload.type === "local-obfuscated") {
      key = await getDerivedKeyForLocal();
    } else if (payload.type === "secure-storage") {
      if (!password) {
        throw new Error("Password is required for 'secure-storage' decryption.");
      }
      if (!payload.salt) {
        throw new Error("Salt is missing in payload for 'secure-storage' decryption.");
      }
      const saltBuffer = base64ToArrayBuffer(payload.salt);
      key = await deriveKeyFromPassword(password, saltBuffer);
    } else {
      throw new Error(`Unsupported payload type: ${payload.type}`);
    }

    const decryptedBuffer = await window.crypto.subtle.decrypt(
      { name: "AES-GCM", iv: iv },
      key,
      encryptedData
    );

    const decryptedString = new TextDecoder().decode(decryptedBuffer);
    return JSON.parse(decryptedString);

  } catch (error) {
    console.error(`Decryption failed for type "${payload.type}":`, error);
    // Provide a more specific error based on context if possible
    let userMessage = "Decryption failed. Data may be corrupted";
    if (payload.type === "local-obfuscated") userMessage += " or the application's internal key has changed.";
    if (payload.type === "secure-storage") userMessage += " or the password was incorrect.";
    throw new Error(userMessage);
  }
}
